import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import 'translations/translations';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';
import { useDispatch } from 'react-redux';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  organizationId: string;
  regionCode?: string;
  inspectorateCode?: string;
};

const useStyles = makeStyles(() => ({
  noMarginBottom: {
    marginBottom: 0,
  },
  lastButton: {
    marginRight: 0,
    marginBottom: 0,
  },
}));

const PrepareOrganizationDialog: React.FC<DialogProps> = (props) => {
  const { isOpen, handleClose, organizationId, regionCode, inspectorateCode } =
    props;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [input, setInput] = useState({
    regionCode: regionCode ?? '',
    inspectorateCode: inspectorateCode ?? '',
  });

  // Reset input when dialog opens or props change
  useEffect(() => {
    if (isOpen) {
      setInput({
        regionCode: regionCode ?? '',
        inspectorateCode: inspectorateCode ?? '',
      });
    }
  }, [isOpen, regionCode, inspectorateCode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  const handlePrepare = () => {
    setIsLoading(true);
    dispatch(
      organizationActions.prepareForest(
        organizationId,
        Number(input.regionCode),
        Number(input.inspectorateCode),
        (error) => {
          setIsLoading(false);
          if (error) {
            dispatch(notificationActions.enqueueErrorResponse(error));
          }
          handleCloseDialog();
        },
      ),
    );
  };

  const isValid =
    input.regionCode !== '' &&
    input.inspectorateCode !== '' &&
    Number(input.regionCode) > 0 &&
    Number(input.inspectorateCode) > 0;

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleCloseDialog();
        }
      }}
    >
      <OverlaySpinner isLoading={isLoading}>
        <DialogTitle disableTypography={true}>
          <Typography variant="h3">
            {t('organization_page.prepare_forest.dialog_title')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="number"
                variant="outlined"
                margin="none"
                fullWidth
                label={t('organization_page.prepare_forest.labels.region_id')}
                name="regionCode"
                value={input.regionCode}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="number"
                variant="outlined"
                margin="none"
                fullWidth
                label={t(
                  'organization_page.prepare_forest.labels.inspectorate_id',
                )}
                name="inspectorateCode"
                value={input.inspectorateCode}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            className={classes.noMarginBottom}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrepare}
            disabled={!isValid}
            className={classes.lastButton}
          >
            {t('organization_page.prepare_forest.prepare_button')}
          </Button>
        </DialogActions>
      </OverlaySpinner>
    </Dialog>
  );
};

export default PrepareOrganizationDialog;
